import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import ModuleReadOnlyNotice from "../../components/reseller/ModuleReadOnlyNotice";
import Pagination from "../../components/shared/Pagination";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    Package,
    Truck,
    CheckCircle2,
    Clock,
    XCircle,
    Search,
    Eye,
    RefreshCw,
    Mail,
    Box,
    Calendar,
    ShoppingCart,
    ExternalLink,
    Loader2,
    MapPin,
    CreditCard,
    User,
    Phone,
    Hash,
    Heart
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE_SIZE = 10;

const statusConfig = {
    pending: { label: "Pending", color: "bg-yellow-100 text-yellow-800", icon: Clock },
    confirmed: { label: "Confirmed", color: "bg-blue-100 text-blue-800", icon: CheckCircle2 },
    processing: { label: "Processing", color: "bg-purple-100 text-purple-800", icon: Package },
    shipped: { label: "Shipped", color: "bg-indigo-100 text-indigo-800", icon: Truck },
    delivered: { label: "Delivered", color: "bg-green-100 text-green-800", icon: CheckCircle2 },
    cancelled: { label: "Cancelled", color: "bg-red-100 text-red-800", icon: XCircle }
};

const paymentStatusConfig = {
    pending: { label: "Pending", color: "bg-yellow-100 text-yellow-800" },
    paid: { label: "Paid", color: "bg-green-100 text-green-800" },
    failed: { label: "Failed", color: "bg-red-100 text-red-800" },
    refunded: { label: "Refunded", color: "bg-slate-100 text-slate-800" }
};

const fulfillmentStatusConfig = {
    unfulfilled: { label: "Unfulfilled", color: "bg-yellow-100 text-yellow-800" },
    processing: { label: "Processing", color: "bg-purple-100 text-purple-800" },
    shipped: { label: "Shipped", color: "bg-indigo-100 text-indigo-800" },
    delivered: { label: "Delivered", color: "bg-green-100 text-green-800" },
    cancelled: { label: "Cancelled", color: "bg-red-100 text-red-800" }
};

export default function ResellerOrders({ store }) {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterStatus, setFilterStatus] = useState("all");
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [showDetails, setShowDetails] = useState(false);
    const [page, setPage] = useState(1);

    const fetchOrders = async () => {
        try {
            setLoading(true);
            // This is the eDisplayroom orders screen — Infinity Touch orders have their
            // own Print Orders page. Without the channel they were listed here too, and
            // double-counted in the earnings cards. `$in: [..,null]` keeps legacy orders.
            const response = await axios.get(`${API}/reseller/orders?channel=edisplay`);
            const data = response.data;
            if (Array.isArray(data)) {
                setOrders(data);
            } else if (data && typeof data === 'object' && Array.isArray(data.orders)) {
                // Handle case where it might be wrapped in an object like { orders: [] }
                setOrders(data.orders);
            } else {
                console.warn("Unexpected orders API response:", data);
                setOrders([]);
            }
        } catch (error) {
            console.error("Fetch orders error:", error);
            toast.error("Failed to load orders");
            setOrders([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchOrders();
    }, []);

    const filteredOrders = orders.filter(order => {
        const statusMatch = filterStatus === "all" || order.status === filterStatus;
        const searchMatch = !searchTerm ||
            order.order_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
            order.customer_email?.toLowerCase().includes(searchTerm.toLowerCase());
        return statusMatch && searchMatch;
    });

    // Filtering shrinks the list, so a stale page would render an empty table.
    useEffect(() => { setPage(1); }, [filterStatus, searchTerm]);

    const totalPages = Math.max(1, Math.ceil(filteredOrders.length / PAGE_SIZE));
    const pagedOrders = filteredOrders.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    const formatDate = (dateStr) => {
        if (!dateStr) return "N/A";
        return new Date(dateStr).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric"
        });
    };

    const formatDateTime = (dateStr) => {
        if (!dateStr) return "N/A";
        return new Date(dateStr).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit"
        });
    };

    return (
        <div className="space-y-6">
            <ModuleReadOnlyNotice module="edisplayroom" />

            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-slate-900">Orders</h1>
                    <p className="text-slate-500">Track and view your store orders</p>
                </div>
                <Button onClick={fetchOrders} variant="outline" size="sm" className="gap-2 rounded-xl">
                    <RefreshCw className="w-4 h-4" />
                    Refresh
                </Button>
            </div>

            {/* Stats Summary */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="card-elevated p-4 bg-white/50 border border-indigo-100/50">
                    <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-1">Total Orders</p>
                    <p className="text-2xl font-bold text-slate-900">{orders.length}</p>
                </div>
                <div className="card-elevated p-4 bg-white/50 border border-indigo-100/50">
                    <p className="text-xs font-bold text-yellow-600 uppercase tracking-widest mb-1">Pending</p>
                    <p className="text-2xl font-bold text-yellow-700">{orders.filter(o => o.status === 'pending').length}</p>
                </div>
                <div className="card-elevated p-4 bg-white/50 border border-indigo-100/50">
                    <p className="text-xs font-bold text-indigo-600 uppercase tracking-widest mb-1">Shipped</p>
                    <p className="text-2xl font-bold text-indigo-700">{orders.filter(o => o.status === 'shipped').length}</p>
                </div>
                <div className="card-elevated p-4 bg-emerald-50 border border-emerald-100/50">
                    <p className="text-xs font-bold text-emerald-600 uppercase tracking-widest mb-1">Net Earnings</p>
                    <p className="text-2xl font-bold text-emerald-700">
                        ${orders.reduce((acc, curr) => acc + (curr.reseller_commission || 0), 0).toFixed(2)}
                    </p>
                </div>
            </div>

            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-4 items-center">
                <div className="relative flex-1 w-full">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                        placeholder="Search by ID or email..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10 rounded-xl"
                    />
                </div>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger className="w-full sm:w-48 rounded-xl">
                        <SelectValue placeholder="All Statuses" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All Statuses</SelectItem>
                        {Object.entries(statusConfig).map(([val, { label }]) => (
                            <SelectItem key={val} value={val}>{label}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            {/* Orders List */}
            <div className="card-elevated overflow-hidden bg-white/80 backdrop-blur-sm border border-indigo-100/50">
                {loading ? (
                    <div className="p-12 text-center">
                        <Loader2 className="w-8 h-8 text-indigo-600 animate-spin mx-auto mb-4" />
                        <p className="text-slate-500 font-medium">Loading orders...</p>
                    </div>
                ) : filteredOrders.length === 0 ? (
                    <div className="p-12 text-center">
                        <ShoppingCart className="w-12 h-12 text-slate-200 mx-auto mb-4" />
                        <p className="text-slate-500 font-medium">No orders found</p>
                        <p className="text-sm text-slate-400 mt-1">Orders will appear here once customers start buying.</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm text-left">
                            <thead className="bg-slate-50/50 text-slate-500 font-medium text-xs uppercase tracking-widest border-b border-slate-100">
                                <tr>
                                    <th className="px-6 py-4">Order Details</th>
                                    <th className="px-6 py-4">Customer</th>
                                    <th className="px-6 py-4">Total Amount</th>
                                    <th className="px-6 py-4">Your Earnings</th>
                                    <th className="px-6 py-4 text-center">Status</th>
                                    <th className="px-6 py-4 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-50">
                                {pagedOrders.map((order) => (
                                    <tr key={order.order_id} className="hover:bg-slate-50/30 transition-colors group">
                                        <td className="px-6 py-4">
                                            <p className="font-mono font-bold text-slate-900">#{order.order_id.slice(-8).toUpperCase()}</p>
                                            <p className="text-[10px] text-slate-400 flex items-center gap-1 mt-1">
                                                <Calendar className="w-3 h-3" /> {formatDate(order.created_at)}
                                            </p>
                                        </td>
                                        <td className="px-6 py-4">
                                            <p className="font-medium text-slate-900">{order.customer_name || 'Guest'}</p>
                                            <p className="text-xs text-slate-500">{order.customer_email}</p>
                                        </td>
                                        <td className="px-6 py-4 font-bold text-slate-900">
                                            ${(order.total_charged || order.total || 0).toFixed(2)}
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className="font-bold text-emerald-600">
                                                +${(order.reseller_commission || 0).toFixed(2)}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-center">
                                            <div className="flex flex-col items-center gap-1">
                                                <Badge className={`rounded-full shadow-none ${statusConfig[order.status]?.color || "bg-slate-100"}`}>
                                                    {statusConfig[order.status]?.label || order.status}
                                                </Badge>
                                                {(order.order_type === 'funeral_purchase' || order.order_type === 'funeral_store') && (
                                                    <Badge variant="outline" className="text-[10px] border-indigo-200 text-indigo-600 bg-indigo-50/50">
                                                        Funeral Supplies Purchase
                                                    </Badge>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="rounded-lg hover:bg-white hover:shadow-sm"
                                                onClick={() => { setSelectedOrder(order); setShowDetails(true); }}
                                            >
                                                <Eye className="w-4 h-4 mr-2" />
                                                View
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            <Pagination
                page={page}
                totalPages={totalPages}
                onChange={setPage}
                totalItems={filteredOrders.length}
                pageSize={PAGE_SIZE}
            />

            {/* Order Details Modal */}
            <Dialog open={showDetails} onOpenChange={setShowDetails}>
                <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col rounded-3xl border-none shadow-2xl p-0 overflow-hidden bg-white">
                    {selectedOrder && (
                        <>
                            {/* Premium Header */}
                            <div className="p-6 bg-gradient-to-r from-indigo-600 to-violet-700 text-white relative overflow-hidden flex-shrink-0">
                                <div className="relative z-10 flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center backdrop-blur-md">
                                            <Package className="w-6 h-6 text-white" />
                                        </div>
                                        <div>
                                            <h3 className="text-2xl font-bold">Order Details</h3>
                                            <p className="text-indigo-100 text-sm font-medium opacity-90">
                                                {selectedOrder.order_number && <span>#{selectedOrder.order_number} • </span>}
                                                #{selectedOrder.order_id.slice(-8).toUpperCase()} • {formatDate(selectedOrder.created_at)}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                {/* Decorative elements */}
                                <div className="absolute top-[-20%] right-[-10%] w-64 h-64 bg-white/10 rounded-full blur-3xl"></div>
                                <div className="absolute bottom-[-20%] left-[-10%] w-32 h-32 bg-indigo-500/20 rounded-full blur-2xl"></div>
                            </div>

                            <div className="p-8 space-y-8 overflow-y-auto flex-1">
                                {/* Order Statuses */}
                                <div className="p-5 bg-slate-50/50 rounded-2xl border border-slate-100/80">
                                    <div className="flex items-center gap-2 mb-4 text-indigo-600">
                                        <Clock className="w-4 h-4" />
                                        <span className="text-[10px] font-bold uppercase tracking-widest">Order Status Details</span>
                                    </div>
                                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                        <div className="bg-white p-3 rounded-xl border border-slate-100 shadow-sm">
                                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">Payment Status</p>
                                            <Badge className={`rounded-md px-2 py-0.5 border-none text-xs font-bold ${paymentStatusConfig[selectedOrder.payment_status]?.color || "bg-slate-100 text-slate-700"}`}>
                                                {paymentStatusConfig[selectedOrder.payment_status]?.label || selectedOrder.payment_status || 'N/A'}
                                            </Badge>
                                        </div>
                                        <div className="bg-white p-3 rounded-xl border border-slate-100 shadow-sm">
                                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">Shipment Status</p>
                                            <Badge className={`rounded-md px-2 py-0.5 border-none text-xs font-bold ${fulfillmentStatusConfig[selectedOrder.fulfillment_status]?.color || "bg-slate-100 text-slate-700"}`}>
                                                {fulfillmentStatusConfig[selectedOrder.fulfillment_status]?.label || selectedOrder.fulfillment_status || 'N/A'}
                                            </Badge>
                                        </div>
                                        <div className="bg-white p-3 rounded-xl border border-slate-100 shadow-sm">
                                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">Order Status</p>
                                            <Badge className={`rounded-md px-2 py-0.5 border-none text-xs font-bold ${statusConfig[selectedOrder.status]?.color || "bg-slate-100 text-slate-700"}`}>
                                                {statusConfig[selectedOrder.status]?.label || selectedOrder.status || 'N/A'}
                                            </Badge>
                                        </div>
                                    </div>
                                </div>

                                {/* Customer & Delivery Info */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="group p-5 bg-slate-50/50 rounded-2xl border border-slate-100/80 transition-all hover:bg-white hover:shadow-md hover:border-indigo-100 min-w-0 overflow-hidden">
                                        <div className="flex items-center gap-2 mb-3 text-indigo-600">
                                            <User className="w-4 h-4" />
                                            <span className="text-[10px] font-bold uppercase tracking-widest">Customer Details</span>
                                        </div>
                                        <p className="text-base font-bold text-slate-900 mb-1 truncate">{selectedOrder.customer_name || 'Guest'}</p>
                                        <p className="text-sm text-slate-500 flex items-center gap-2 truncate">
                                            <Mail className="w-3.5 h-3.5 flex-shrink-0" /> <span className="truncate">{selectedOrder.customer_email}</span>
                                        </p>
                                        {selectedOrder.contact_email && selectedOrder.contact_email !== selectedOrder.customer_email && (
                                            <p className="text-sm text-slate-500 flex items-center gap-2 truncate mt-1">
                                                <Mail className="w-3.5 h-3.5 flex-shrink-0" /> <span className="truncate">{selectedOrder.contact_email} (contact)</span>
                                            </p>
                                        )}
                                        {selectedOrder.is_guest && (
                                            <Badge className="mt-2 bg-slate-100 text-slate-600 text-[10px] rounded-md">Guest Checkout</Badge>
                                        )}
                                    </div>

                                    {/* Delivery Info (Funeral specific) */}
                                    {(selectedOrder.deceased_name || selectedOrder.delivery_date || selectedOrder.delivery_time) ? (
                                        <div className="group p-5 bg-slate-50/50 rounded-2xl border border-slate-100/80 transition-all hover:bg-white hover:shadow-md hover:border-indigo-100 min-w-0 overflow-hidden">
                                            <div className="flex items-center gap-2 mb-3 text-indigo-600">
                                                <Heart className="w-4 h-4" />
                                                <span className="text-[10px] font-bold uppercase tracking-widest">Deceased Name</span>
                                            </div>
                                            {selectedOrder.deceased_name && (
                                                <p className="text-base font-bold text-slate-900 mb-1">In Memory of: {selectedOrder.deceased_name}</p>
                                            )}
                                            {selectedOrder.delivery_date && (
                                                <p className="text-sm text-slate-500 flex items-center gap-2 mt-1">
                                                    <Calendar className="w-3.5 h-3.5 flex-shrink-0" /> {formatDate(selectedOrder.delivery_date)}
                                                </p>
                                            )}
                                            {selectedOrder.delivery_time && (
                                                <p className="text-sm text-slate-500 flex items-center gap-2 mt-1">
                                                    <Clock className="w-3.5 h-3.5 flex-shrink-0" /> {selectedOrder.delivery_time}
                                                </p>
                                            )}
                                        </div>
                                    ) : (
                                        <div className="group p-5 bg-slate-50/50 rounded-2xl border border-slate-100/80 transition-all hover:bg-white hover:shadow-md hover:border-indigo-100 min-w-0 overflow-hidden">
                                            <div className="flex items-center gap-2 mb-3 text-indigo-600">
                                                <Truck className="w-4 h-4" />
                                                <span className="text-[10px] font-bold uppercase tracking-widest">Shipping Method</span>
                                            </div>
                                            {selectedOrder.shipping_method ? (
                                                <p className="text-base font-bold text-slate-900 mb-1 break-words leading-tight">{selectedOrder.shipping_method}</p>
                                            ) : (
                                                <p className="text-sm text-slate-400 italic font-medium">Standard Delivery</p>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {/* Shipping Address */}
                                {selectedOrder.shipping_address && (
                                    <div className="p-5 bg-slate-50/50 rounded-2xl border border-slate-100/80">
                                        <div className="flex items-center gap-2 mb-3 text-indigo-600">
                                            <MapPin className="w-4 h-4" />
                                            <span className="text-[10px] font-bold uppercase tracking-widest">Shipping Address</span>
                                        </div>
                                        <div className="text-sm text-slate-700 space-y-0.5">
                                            {selectedOrder.shipping_address.name && <p className="font-bold text-slate-900">{selectedOrder.shipping_address.name}</p>}
                                            {selectedOrder.shipping_address.line1 && <p>{selectedOrder.shipping_address.line1}</p>}
                                            {selectedOrder.shipping_address.line2 && <p>{selectedOrder.shipping_address.line2}</p>}
                                            <p>
                                                {[selectedOrder.shipping_address.city, selectedOrder.shipping_address.state, selectedOrder.shipping_address.postal_code].filter(Boolean).join(', ')}
                                            </p>
                                            {selectedOrder.shipping_address.country && selectedOrder.shipping_address.country !== 'US' && (
                                                <p>{selectedOrder.shipping_address.country}</p>
                                            )}
                                            {selectedOrder.shipping_address.phone && (
                                                <p className="flex items-center gap-1.5 mt-1 text-slate-500">
                                                    <Phone className="w-3.5 h-3.5" /> {selectedOrder.shipping_address.phone}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Tracking Info */}
                                {(selectedOrder.tracking_number || selectedOrder.carrier) && (
                                    <div className="p-5 bg-indigo-50/50 rounded-2xl border border-indigo-100/50">
                                        <div className="flex items-center gap-2 mb-3 text-indigo-600">
                                            <Truck className="w-4 h-4" />
                                            <span className="text-[10px] font-bold uppercase tracking-widest">Tracking</span>
                                        </div>
                                        <div className="flex items-center gap-3 flex-wrap">
                                            {selectedOrder.carrier && (
                                                <Badge className="bg-indigo-100 text-indigo-800 rounded-lg">{selectedOrder.carrier.toUpperCase()}</Badge>
                                            )}
                                            {selectedOrder.tracking_number && (
                                                <span className="text-sm font-mono font-bold text-indigo-700">{selectedOrder.tracking_number}</span>
                                            )}
                                            {selectedOrder.tracking_number && (
                                                <a
                                                    href={selectedOrder.tracking_url || `https://www.google.com/search?q=${selectedOrder.carrier || ''}+tracking+${selectedOrder.tracking_number}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-indigo-500 hover:text-indigo-700"
                                                >
                                                    <ExternalLink className="w-4 h-4" />
                                                </a>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Product Items */}
                                <div>
                                    <div className="flex items-center justify-between mb-4 px-1">
                                        <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Ordered Products ({selectedOrder.items?.length || 0})</h4>
                                    </div>
                                    <div className="space-y-3">
                                        {selectedOrder.items?.map((item, idx) => (
                                            <div key={idx} className="flex items-center gap-4 p-4 bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-md transition-shadow">
                                                <div className="w-14 h-14 bg-slate-50 rounded-xl flex items-center justify-center border border-slate-50 overflow-hidden flex-shrink-0">
                                                    {item.image_url ? (
                                                        <img src={item.image_url} alt="" className="w-full h-full object-cover p-1" />
                                                    ) : (
                                                        <Box className="w-6 h-6 text-slate-300" />
                                                    )}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm font-bold text-slate-900 mb-1 leading-tight">{item.name}</p>
                                                    <p className="text-xs text-slate-500 font-medium">Qty: {item.quantity} • Unit: ${(item.display_price || 0).toFixed(2)}</p>

                                                    {/* Customizations display */}
                                                    {item.customizations && (Array.isArray(item.customizations) ? item.customizations.length > 0 : Object.keys(item.customizations).length > 0) && (
                                                        <div className="mt-2 space-y-1.5 pl-2 border-l-2 border-slate-100 py-0.5">
                                                            {Array.isArray(item.customizations) ? (
                                                                item.customizations.map((cust) => (
                                                                    <div key={cust.id} className="text-[10px]">
                                                                        <div className="flex justify-between font-bold text-slate-600">
                                                                            <span>{cust.name}</span>
                                                                            <span className="text-indigo-600">+${cust.total.toFixed(2)}</span>
                                                                        </div>
                                                                        {cust.fields.map((f, i) => (
                                                                            <p key={i} className="text-slate-400 italic leading-tight">
                                                                                {f.label}: {f.value}
                                                                            </p>
                                                                        ))}
                                                                    </div>
                                                                ))
                                                            ) : (
                                                                Object.entries(item.customizations).map(([label, val]) => (
                                                                    <p key={label} className="text-[10px] text-slate-400 italic">
                                                                        <span className="font-medium mr-1 text-slate-500">{label}:</span>
                                                                        {val && typeof val === 'string' && val.includes('T') && !isNaN(Date.parse(val))
                                                                            ? new Date(val).toLocaleDateString()
                                                                            : val === true ? 'Yes' : val}
                                                                    </p>
                                                                ))
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="text-right">
                                                    <p className="text-sm font-black text-slate-900">${((item.display_price || 0) * (item.quantity || 1)).toFixed(2)}</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Reseller Stats - Financial Summary */}
                                <div className="bg-emerald-50 rounded-3xl p-8 border border-emerald-100/50 shadow-inner overflow-hidden relative">
                                    {/* Glass reflection */}
                                    <div className="absolute top-0 right-0 w-32 h-32 bg-white/40 blur-2xl rotate-45 transform translate-x-12 -translate-y-12"></div>

                                    <div className="relative z-10 space-y-5">
                                        <div className="flex justify-between items-center text-sm">
                                            <span className="text-emerald-700/80 font-bold uppercase tracking-wider text-[10px]">Merchant Subtotal</span>
                                            <span className="font-mono font-bold text-emerald-900 text-base">${(selectedOrder.subtotal || 0).toFixed(2)}</span>
                                        </div>
                                        <div className="flex justify-between items-center text-sm">
                                            <span className="text-emerald-700/80 font-bold uppercase tracking-wider text-[10px]">Customer Shipping</span>
                                            <span className="font-mono font-bold text-emerald-900 text-base">${(selectedOrder.shipping || selectedOrder.shipping_cost || 0).toFixed(2)}</span>
                                        </div>

                                        <div className="h-px bg-emerald-200/40 w-full"></div>

                                        <div className="flex justify-between items-end">
                                            <div>
                                                <p className="text-[10px] font-black text-emerald-800 uppercase tracking-[0.2em] mb-1">Total Charged</p>
                                                <div className="flex items-center gap-2">
                                                    <Badge className={`text-[10px] px-2 py-0 ${paymentStatusConfig[selectedOrder.payment_status]?.color || 'bg-emerald-600'}`}>
                                                        {paymentStatusConfig[selectedOrder.payment_status]?.label || 'Paid'}
                                                    </Badge>
                                                    <div className="text-xs text-emerald-600 font-medium">via Stripe</div>
                                                </div>
                                            </div>
                                            <p className="text-3xl font-black text-emerald-900 tracking-tight">
                                                <span className="text-lg mr-0.5">$</span>
                                                {(selectedOrder.total_charged || selectedOrder.total || 0).toFixed(2)}
                                            </p>
                                        </div>

                                        <div className="pt-6">
                                            <div className="bg-white/80 backdrop-blur-md p-6 rounded-2xl border border-white shadow-xl">
                                                <div className="flex justify-between items-center">
                                                    <div>
                                                        <p className="text-[10px] font-black text-emerald-600 uppercase tracking-widest mb-1">Your Net Dividend</p>
                                                        <p className="text-[10px] text-slate-400 font-medium italic">Commission earned from this sale.</p>
                                                    </div>
                                                    <div className="text-right">
                                                        <div className="text-2xl font-black text-emerald-700 font-mono">
                                                            <span className="text-sm mr-1">+</span>
                                                            ${(selectedOrder.reseller_commission || 0).toFixed(2)}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Timestamps */}
                                <div className="text-xs text-slate-400 space-y-1 px-1">
                                    <p>Created: {formatDateTime(selectedOrder.created_at)}</p>
                                    {selectedOrder.paid_at && <p>Paid: {formatDateTime(selectedOrder.paid_at)}</p>}
                                    {selectedOrder.shipped_at && <p>Shipped: {formatDateTime(selectedOrder.shipped_at)}</p>}
                                    {selectedOrder.delivered_at && <p>Delivered: {formatDateTime(selectedOrder.delivered_at)}</p>}
                                </div>
                            </div>
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
